import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Minus,
  Tractor,
  Leaf,
  Droplet,
  Settings,
  Search,
  Edit,
  Trash2,
  Package,
  Wrench,
  Building2,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de matériel avec icônes
const TYPES_MATERIEL = [
  { id: 'tracteur', nom: 'Tracteur', icon: Tractor },
  { id: 'outil', nom: 'Outil attelé', icon: Wrench },
  { id: 'pulverisateur', nom: 'Pulvérisateur', icon: Droplet },
  { id: 'semoir', nom: 'Semoir', icon: Leaf },
  { id: 'remorque', nom: 'Remorque', icon: Package },
  { id: 'moissonneuse', nom: 'Moissonneuse', icon: Tractor },
  { id: 'autre', nom: 'Autre', icon: Settings },
];

// Dialog pour ajouter/modifier du matériel
const MaterielDialog = ({ open, onOpenChange, materiel, onSave }) => {
  const [formData, setFormData] = useState({
    nom: '',
    type_materiel: 'tracteur',
    marque: '',
    modele: '',
    annee: '',
    immatriculation: '',
    puissance: '',
    largeur_travail: '',
    capacite: '',
    cout_horaire: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (materiel) {
      setFormData({
        nom: materiel.nom || '',
        type_materiel: materiel.type_materiel || 'tracteur',
        marque: materiel.marque || '',
        modele: materiel.modele || '',
        annee: materiel.annee || '',
        immatriculation: materiel.immatriculation || '',
        puissance: materiel.puissance || '',
        largeur_travail: materiel.largeur_travail || '',
        capacite: materiel.capacite || '',
        cout_horaire: materiel.cout_horaire || '',
        notes: materiel.notes || '',
      });
    } else {
      setFormData({
        nom: '', type_materiel: 'tracteur', marque: '', modele: '',
        annee: '', immatriculation: '', puissance: '', largeur_travail: '',
        capacite: '', cout_horaire: '', notes: '',
      });
    }
  }, [materiel, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    await onSave(formData, materiel?.id);
    setSaving(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{materiel ? 'Modifier le matériel' : 'Ajouter du matériel'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData(p => ({ ...p, nom: e.target.value }))}
                placeholder="Ex: John Deere 6130R"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type_materiel} onValueChange={(v) => setFormData(p => ({ ...p, type_materiel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES_MATERIEL.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marque</Label>
              <Input value={formData.marque} onChange={(e) => setFormData(p => ({ ...p, marque: e.target.value }))} placeholder="John Deere" />
            </div>
            <div className="space-y-2">
              <Label>Modèle</Label>
              <Input value={formData.modele} onChange={(e) => setFormData(p => ({ ...p, modele: e.target.value }))} placeholder="6130R" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Année</Label>
              <Input type="number" value={formData.annee} onChange={(e) => setFormData(p => ({ ...p, annee: e.target.value }))} placeholder="2020" />
            </div>
            <div className="space-y-2">
              <Label>Puissance (CV)</Label>
              <Input type="number" value={formData.puissance} onChange={(e) => setFormData(p => ({ ...p, puissance: e.target.value }))} placeholder="130" />
            </div>
            <div className="space-y-2">
              <Label>Coût/h (€)</Label>
              <Input type="number" step="0.01" value={formData.cout_horaire} onChange={(e) => setFormData(p => ({ ...p, cout_horaire: e.target.value }))} placeholder="45.00" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving}>
              <Plus className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : (materiel ? 'Modifier' : 'Ajouter')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog pour ajouter un type d'opération
const TypeOperationDialog = ({ open, onOpenChange, categories, onSave }) => {
  const [formData, setFormData] = useState({ nom: '', categorie: 'travail_sol', couleur: '#22c55e' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
    onOpenChange(false);
    setFormData({ nom: '', categorie: 'travail_sol', couleur: '#22c55e' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter un type d'opération</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nom *</Label>
            <Input value={formData.nom} onChange={(e) => setFormData(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: Roulage" required />
          </div>
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={formData.categorie} onValueChange={(v) => setFormData(p => ({ ...p, categorie: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                      {c.nom}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Couleur</Label>
            <div className="flex gap-2">
              <Input type="color" value={formData.couleur} onChange={(e) => setFormData(p => ({ ...p, couleur: e.target.value }))} className="w-12 h-10 p-1" />
              <Input value={formData.couleur} onChange={(e) => setFormData(p => ({ ...p, couleur: e.target.value }))} className="flex-1" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving}>
              <Plus className="w-4 h-4 mr-2" />
              {saving ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog pour ajouter un intrant
const IntrantDialog = ({ open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState({ nom: '', type_intrant: 'phyto', unite: 'L', code_amm: '', matiere_active: '', fournisseur: '', prix_unitaire: '', stock_actuel: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
    onOpenChange(false);
    setFormData({ nom: '', type_intrant: 'phyto', unite: 'L', code_amm: '', matiere_active: '', fournisseur: '', prix_unitaire: '', stock_actuel: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter un intrant</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={formData.nom} onChange={(e) => setFormData(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: Roundup" required />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type_intrant} onValueChange={(v) => setFormData(p => ({ ...p, type_intrant: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phyto">Phytosanitaire</SelectItem>
                  <SelectItem value="engrais">Engrais</SelectItem>
                  <SelectItem value="semence">Semence</SelectItem>
                  <SelectItem value="amendement">Amendement</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Unité</Label>
              <Select value={formData.unite} onValueChange={(v) => setFormData(p => ({ ...p, unite: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Litre (L)</SelectItem>
                  <SelectItem value="kg">Kilogramme (kg)</SelectItem>
                  <SelectItem value="dose">Dose</SelectItem>
                  <SelectItem value="unite">Unité</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prix unitaire (€)</Label>
              <Input type="number" step="0.01" value={formData.prix_unitaire} onChange={(e) => setFormData(p => ({ ...p, prix_unitaire: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Stock actuel</Label>
              <Input type="number" step="0.1" value={formData.stock_actuel} onChange={(e) => setFormData(p => ({ ...p, stock_actuel: e.target.value }))} />
            </div>
          </div>
          {formData.type_intrant === 'phyto' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code AMM</Label>
                <Input value={formData.code_amm} onChange={(e) => setFormData(p => ({ ...p, code_amm: e.target.value }))} placeholder="Ex: 2000123" />
              </div>
              <div className="space-y-2">
                <Label>Matière active</Label>
                <Input value={formData.matiere_active} onChange={(e) => setFormData(p => ({ ...p, matiere_active: e.target.value }))} placeholder="Ex: Glyphosate" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving}>
              <Plus className="w-4 h-4 mr-2" />
              {saving ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function ReferentielsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('materiels');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data
  const [materiels, setMateriels] = useState([]);
  const [typesOperations, setTypesOperations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [intrants, setIntrants] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialogs
  const [materielDialogOpen, setMaterielDialogOpen] = useState(false);
  const [editingMateriel, setEditingMateriel] = useState(null);
  const [typeOpDialogOpen, setTypeOpDialogOpen] = useState(false);
  const [intrantDialogOpen, setIntrantDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [matRes, typesRes, intrantsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/referentiels/materiels`, { headers }),
        fetch(`${API_URL}/api/referentiels/types-operations`, { headers }),
        fetch(`${API_URL}/api/referentiels/intrants`, { headers }),
        fetch(`${API_URL}/api/referentiels/stats/recoupement`, { headers }),
      ]);

      if (matRes.ok) {
        const data = await matRes.json();
        setMateriels(data.materiels || []);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setTypesOperations(data.types_operations || []);
        setCategories(data.categories || []);
      }
      if (intrantsRes.ok) {
        const data = await intrantsRes.json();
        setIntrants(data.intrants || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // CRUD Matériel
  const handleSaveMateriel = async (data, materielId) => {
    try {
      const url = materielId 
        ? `${API_URL}/api/referentiels/materiels/${materielId}`
        : `${API_URL}/api/referentiels/materiels`;
      
      const res = await fetch(url, {
        method: materielId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success(materielId ? 'Matériel modifié' : 'Matériel ajouté');
        fetchData();
        setEditingMateriel(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteMateriel = async (id) => {
    if (!confirm('Supprimer ce matériel ?')) return;
    try {
      await fetch(`${API_URL}/api/referentiels/materiels/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Matériel supprimé');
      setMateriels(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // CRUD Type opération
  const handleSaveTypeOp = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/referentiels/types-operations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success('Type d\'opération ajouté');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteTypeOp = async (id) => {
    if (!confirm('Supprimer ce type ?')) return;
    try {
      await fetch(`${API_URL}/api/referentiels/types-operations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Type supprimé');
      setTypesOperations(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // CRUD Intrant
  const handleSaveIntrant = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/referentiels/intrants`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success('Intrant ajouté');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteIntrant = async (id) => {
    if (!confirm('Supprimer cet intrant ?')) return;
    try {
      await fetch(`${API_URL}/api/referentiels/intrants/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Intrant supprimé');
      setIntrants(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Filtrage
  const filteredMateriels = materiels.filter(m => 
    m.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.marque?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTypesOp = typesOperations.filter(t =>
    t.nom?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredIntrants = intrants.filter(i =>
    i.nom?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referentiels-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Référentiels</h1>
          <p className="text-muted-foreground">Gérez votre matériel, types d'opérations et intrants</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Tractor className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{stats.totaux?.materiels || 0}</p>
              <p className="text-xs text-muted-foreground">Matériels</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Package className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{stats.totaux?.parcelles || 0}</p>
              <p className="text-xs text-muted-foreground">Parcelles</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{stats.totaux?.interventions || 0}</p>
              <p className="text-xs text-muted-foreground">Interventions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Droplet className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="text-2xl font-bold">{stats.totaux?.intrants || 0}</p>
              <p className="text-xs text-muted-foreground">Intrants</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 text-teal-500" />
              <p className="text-2xl font-bold">{stats.totaux?.collaborateurs || 0}</p>
              <p className="text-xs text-muted-foreground">Collaborateurs</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="materiels">
            <Tractor className="w-4 h-4 mr-2" />
            Matériel ({materiels.length})
          </TabsTrigger>
          <TabsTrigger value="operations">
            <Settings className="w-4 h-4 mr-2" />
            Types d'opérations ({typesOperations.length})
          </TabsTrigger>
          <TabsTrigger value="intrants">
            <Droplet className="w-4 h-4 mr-2" />
            Intrants ({intrants.length})
          </TabsTrigger>
        </TabsList>

        {/* Matériels */}
        <TabsContent value="materiels" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingMateriel(null); setMaterielDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter du matériel
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Marque/Modèle</TableHead>
                  <TableHead>Puissance</TableHead>
                  <TableHead>Coût/h</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMateriels.map((m) => {
                  const TypeIcon = TYPES_MATERIEL.find(t => t.id === m.type_materiel)?.icon || Tractor;
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <TypeIcon className="w-4 h-4 text-muted-foreground" />
                          {m.nom}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {TYPES_MATERIEL.find(t => t.id === m.type_materiel)?.nom || m.type_materiel}
                        </Badge>
                      </TableCell>
                      <TableCell>{m.marque} {m.modele}</TableCell>
                      <TableCell>{m.puissance ? `${m.puissance} CV` : '-'}</TableCell>
                      <TableCell>{m.cout_horaire ? `${m.cout_horaire} €/h` : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditingMateriel(m); setMaterielDialogOpen(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteMateriel(m.id)}>
                            <Minus className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredMateriels.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Aucun matériel. Cliquez sur + pour en ajouter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Types d'opérations */}
        <TabsContent value="operations" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setTypeOpDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un type
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const catTypes = filteredTypesOp.filter(t => t.categorie === cat.id);
              return (
                <Card key={cat.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.nom}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {catTypes.map((t) => (
                        <div key={t.id} className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted">
                          <span className="text-sm">{t.nom}</span>
                          {t.is_custom && (
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => handleDeleteTypeOp(t.id)}>
                              <Minus className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {catTypes.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">Aucun type</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Intrants */}
        <TabsContent value="intrants" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setIntrantDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un intrant
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Unité</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIntrants.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-medium">{i.nom}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{i.type_intrant}</Badge>
                    </TableCell>
                    <TableCell>{i.unite}</TableCell>
                    <TableCell>{i.prix_unitaire ? `${i.prix_unitaire} €` : '-'}</TableCell>
                    <TableCell>{i.stock_actuel ? `${i.stock_actuel} ${i.unite}` : '-'}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteIntrant(i.id)}>
                        <Minus className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredIntrants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Aucun intrant. Cliquez sur + pour en ajouter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <MaterielDialog
        open={materielDialogOpen}
        onOpenChange={setMaterielDialogOpen}
        materiel={editingMateriel}
        onSave={handleSaveMateriel}
      />
      <TypeOperationDialog
        open={typeOpDialogOpen}
        onOpenChange={setTypeOpDialogOpen}
        categories={categories}
        onSave={handleSaveTypeOp}
      />
      <IntrantDialog
        open={intrantDialogOpen}
        onOpenChange={setIntrantDialogOpen}
        onSave={handleSaveIntrant}
      />
    </div>
  );
}
